<template>
  <v-menu
    ref="menu"
    v-model="menu"
    :close-on-content-click="false"
    transition="scale-transition"
    offset-y
    max-width="290px"
    min-width="290px"
  >
    <template v-slot:activator="{ on }">
      <v-text-field
        v-on="on"
        :label="label"
        :value="textFieldDate"
        :disabled="disabled"
        @input="textFieldInput"
        @change="textFieldChange"
        :hide-details="hideDetails"
        :append-icon="appendIcon"
        :append-outer-icon="appendOuterIcon"
        :clearable="clearable"
        :background-color="backgroundColor"
        :dense="dense"
        :error="error"
        :error-messages="errorMessages"
        :hint="hint"
        :nudge-right="prependIcon ? '33px' : null"
        :persistent-hint="persistentHint"
        :prepend-icon="prependIcon"
        :prepend-inner-icon="prependInnerIcon"
        :readonly="readonly"
        :rules="dateRules"
        @click:clear="clear"
        autocomplete="false"
      >
      </v-text-field>
    </template>
    <v-date-picker
      v-if="menu && !readonly"
      :max="max"
      :min="min"
      :value="pickerDate"
      @input="datePickerInput"
      @change="datePickerChange"
    >
    </v-date-picker>
  </v-menu>
</template>
<script>
import { DateTime } from 'luxon'

const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd'

export default {
  name: 'DateInput',
  props: {
    value: {
      type: String,
      description: 'ISO date string'
    },
    displayFormat: {
      type: String,
      default: DEFAULT_DATE_FORMAT
    },
    appendIcon: String,
    appendOuterIcon: String,
    backgroundColor: String,
    clearable: Boolean,
    dense: Boolean,
    disabled: Boolean,
    error: Boolean,
    errorMessages: [String, Array],
    hideDetails: [Boolean, String],
    hint: String,
    label: String,
    persistentHint: Boolean,
    prependIcon: String,
    prependInnerIcon: String,
    readonly: Boolean,
    rules: Array,
    max: {
      type: String,
      description: 'The maximum allowed date in ISO date format'
    },
    min: {
      type: String,
      description: 'The minimum allowed date in ISO date format'
    }
  },
  data() {
    return {
      menu: false,
      originalDate: null,
      date: null,
      valueIsString: false
    }
  },
  components: {},
  computed: {
    textFieldDate() {
      return this.date
        ? this.date.set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }).toFormat(this.displayFormat)
        : ''
    },
    pickerDate() {
      return this.date
        ? this.date.set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }).toFormat(this.displayFormat)
        : ''
    },
    dirty() {
      if (!this.originalDate) {
        return !!this.date
      }
      return !this.date.equals(this.originalDate)
    },
    dateRules() {
      return this.rules ? [...this.rules, this.isDate] : [this.isDate]
    }
  },
  watch: {
    value: {
      handler: function () {
        let dt = DateTime.fromISO(this.value, { zone: 'utc' }).set({ hour: 0, minute: 0, second: 0, milliseconds: 0 })
        this.originalDate = this.date = dt.isValid ? dt : null
      },
      immediate: true
    },
    menu() {
      if (!this.menu && this.dirty) {
        this.$emit(
          'change',
          this.date
            .toUTC()
            .set({ hour: 0, minute: 0, second: 0, milliseconds: 0 })
            .toISO({ suppressMilliseconds: true })
        )
      }
    }
  },
  methods: {
    clear(event) {
      if (!this.readonly) this.$emit('click:clear', event)
    },
    datePickerInput(newDate) {
      this.date = DateTime.fromISO(newDate, { zone: 'utc' })
      this.$emit(
        'input',
        this.date.toUTC().set({ hour: 0, minute: 0, second: 0, milliseconds: 0 }).toISO({ suppressMilliseconds: true })
      )
    },
    datePickerChange(newDate) {
      this.date = DateTime.fromISO(newDate, { zone: 'utc' }).set({ hour: 0, minute: 0, second: 0, milliseconds: 0 })
      this.menu = false
    },
    textFieldInput(newDateText) {
      if (!newDateText) {
        //  This is to handle the case of the user clearing the date either manually or with the X
        this.date = null
        this.$emit('input', null)
        return
      }
      let dt = DateTime.fromFormat(newDateText, this.displayFormat, { zone: 'utc' })
      if (dt.isValid) {
        this.date = dt
        this.$emit(
          'input',
          this.date
            .toUTC()
            .set({ hour: 0, minute: 0, second: 0, milliseconds: 0 })
            .toISO({ suppressMilliseconds: true })
        )
      }
    },
    textFieldChange() {
      if (this.date === null || this.date === undefined) {
        this.$emit('change', null)
      } else {
        this.$emit('change', this.date.toUTC().set({ milliseconds: 0 }).toISO({ suppressMilliseconds: true }))
      }
    },
    isDate(value) {
      if (!value) {
        return true
      }
      let dt = DateTime.fromFormat(value, this.displayFormat, { zone: 'utc' })
      return dt.isValid || 'Please enter a date.'
    },
    reset() {
      this.date = this.originalDate
    }
  }
}
</script>
